import React, { useCallback } from 'react';

import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { TrackChangesPlugin } from '@atlaskit/editor-plugin-track-changes';

import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { fg } from '@confluence/feature-gating';

import { useSharedAIState } from './hooks/useSharedAIState.ts';

interface ObjectSidebarControlFullPageEditorProps {
	editorApi: PublicPluginAPI<[TrackChangesPlugin]> | undefined;
	editorActionsProps: Parameters<typeof ObjectSidebarControl>[0]['undoRedoButtonActions'];
	hasAiButton: boolean;
	hasAudioButton: boolean;
	contentId: string;
	spaceKey: string;
	contentType: string;
	contentTitle: string;
	isUnpublishedDraft?: boolean;
	disabled?: boolean;
}

export function ObjectSidebarControlFullPageEditor({
	editorApi,
	editorActionsProps,
	hasAiButton,
	hasAudioButton,
	contentId,
	spaceKey,
	contentType,
	contentTitle,
	isUnpublishedDraft,
	disabled,
}: ObjectSidebarControlFullPageEditorProps) {
	const isShowDiffSelected =
		useSharedPluginStateSelector(editorApi, 'trackChanges.isDisplayingChanges', {
			disabled: !fg('platform_editor_ai_aifc_streaming'),
		}) ?? false;
	const isShowDiffAvailable =
		useSharedPluginStateSelector(editorApi, 'trackChanges.isShowDiffAvailable', {
			disabled: !fg('platform_editor_ai_aifc_streaming'),
		}) ?? false;

	const onShowDiff = useCallback(() => {
		const wasShowDiffSelected = isShowDiffSelected;
		const result = editorApi?.core.actions.execute(editorApi?.trackChanges?.commands.toggleChanges);
		// On de-selection - focus back on the editor
		if (result && wasShowDiffSelected) {
			editorApi?.core.actions.focus();
		}
	}, [editorApi, isShowDiffSelected]);

	const { aiIsStreaming } = useSharedAIState(editorApi);

	return (
		<ObjectSidebarControl
			key="object-sidebar-control"
			hasCommentsButton
			hasDetailsButton
			hasLoomButton
			hasUndoRedoButton
			undoRedoButtonActions={editorActionsProps}
			hasAiButton={hasAiButton}
			hasAudioButton={hasAudioButton}
			showDiffButtonActions={{
				isShowDiffEnabled: isShowDiffAvailable ?? false,
				onShowDiff,
				isShowDiffSelected,
			}}
			aiIsStreaming={aiIsStreaming}
			contentId={contentId}
			spaceKey={spaceKey}
			contentType={contentType}
			contentTitle={contentTitle}
			isUnpublishedDraft={isUnpublishedDraft}
			disabled={disabled}
		/>
	);
}
